import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  SimpleTable,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import Tile from "@src/components/containers/Tile";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";
import StatusBadge from "@src/components/common/StatusBadge";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { Clinic, ClinicUser } from "@src/types/clinic";
import { ClinicUserStatuses } from "@src/constants/ClinicUserConstants";
import AddClinicManualModal from "./AddClinicManualModal";
import styles from "./ManageClinicPage.module.scss";

function formatAddress(clinic: Clinic): string {
  const parts = [clinic.address1, clinic.city, clinic.state]
    .filter((p) => !!p && p.trim().length > 0)
    .join(", ");
  return [parts, clinic.zipCode].filter(Boolean).join(" ").trim();
}

export default function ManageClinicPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentUser, setActiveClinic, setDefaultClinic, removeClinic } =
    useCurrentUserStore();
  const clinicUsers = currentUser?.clinicUsers ?? [];

  const [manualOpen, setManualOpen] = useState(false);

  const handleShopNow = (clinicId: string) => {
    setActiveClinic(clinicId, () => navigate("/"));
  };

  const handleMakePrimary = async (clinicId: string) => {
    try {
      await setDefaultClinic(clinicId);
      showToast({ message: "Primary clinic updated.", variant: "success" });
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not update primary clinic.",
        variant: "error",
      });
    }
  };

  const handleRemoveClinic = async (clinicUser: ClinicUser) => {
    const confirmed = window.confirm(
      `Remove ${clinicUser.clinic.name} from your account? You will lose access to its orders and team.`,
    );
    if (!confirmed) return;

    try {
      await removeClinic(clinicUser.clinic.id);
      showToast({
        message: `${clinicUser.clinic.name} removed.`,
        variant: "success",
      });
    } catch (err: any) {
      showToast({
        message:
          err?.response?.body?.error ||
          err?.message ||
          "Could not remove clinic.",
        variant: "error",
      });
    }
  };

  return (
    <div className={styles.root} data-testid="manage-clinic-page">
      <Tile>
        <div className={styles.header}>
          <h1 className={styles.title}>Manage Clinics</h1>
          <Button
            id="manage-clinic-page-add-clinic-button"
            className="btn btn-primary btn-sm"
            onClick={() => setManualOpen(true)}
            data-testid="manage-clinic-page-add-clinic-button"
          >
            Add Clinic
          </Button>
        </div>

        <SimpleTable
          className={styles.table}
          columns={[
            {
              header: "Clinic",
              key: "clinicName",
              render: (clinicUser: ClinicUser) => (
                <span className={styles.clinicCell}>
                  <span className={styles.clinicName}>
                    {clinicUser.clinic.name}
                  </span>
                  {clinicUser.isDefault && (
                    <i
                      className={`fa-solid fa-star-sharp ${styles.primaryStar}`}
                      aria-label="Primary clinic"
                    />
                  )}
                  {clinicUser.status === ClinicUserStatuses.Pending && (
                    <StatusBadge variant="pending" label="Pending approval" />
                  )}
                </span>
              ),
            },
            {
              header: "Address",
              key: "address",
              render: (clinicUser: ClinicUser) => {
                const address = formatAddress(clinicUser.clinic);
                return <span className={styles.address}>{address || "—"}</span>;
              },
            },
            {
              header: "",
              key: "shopNow",
              render: (clinicUser: ClinicUser) => (
                <Button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleShopNow(clinicUser.clinic.id)}
                  data-testid={`manage-clinic-page-shop-now-button-${clinicUser.clinic.id}`}
                >
                  Shop Now
                </Button>
              ),
            },
            {
              header: "",
              key: "actions",
              render: (clinicUser: ClinicUser) => {
                const isActive =
                  clinicUser.status === ClinicUserStatuses.Active;
                return (
                  <span className="padding-left-sm">
                    <Menu
                      buttonContent={
                        <div className="padding-x-sm">
                          <i
                            className={`fa-light fa-ellipsis-v ${styles.kebab}`}
                          />
                        </div>
                      }
                      theme={MenuThemes.Small}
                    >
                      {isActive && (
                        <MenuItem>
                          <Button
                            className="width-100"
                            label="Edit"
                            onClick={() =>
                              navigate(`/manage-clinic/${clinicUser.clinic.id}`)
                            }
                            data-testid={`manage-clinic-page-edit-${clinicUser.clinic.id}`}
                          />
                        </MenuItem>
                      )}
                      {isActive && !clinicUser.isDefault && (
                        <MenuItem>
                          <Button
                            className="width-100"
                            label="Make Primary"
                            onClick={() =>
                              handleMakePrimary(clinicUser.clinic.id)
                            }
                            data-testid={`manage-clinic-page-make-primary-${clinicUser.clinic.id}`}
                          />
                        </MenuItem>
                      )}
                      <MenuItem>
                        <Button
                          className="width-100"
                          label="Remove"
                          color="#F80A33"
                          hoverColor="#FF98AA"
                          onClick={() => handleRemoveClinic(clinicUser)}
                          data-testid={`manage-clinic-page-remove-${clinicUser.clinic.id}`}
                        />
                      </MenuItem>
                    </Menu>
                  </span>
                );
              },
            },
          ]}
          data={clinicUsers}
        />
      </Tile>

      <AddClinicManualModal
        isShowing={manualOpen}
        onClose={() => setManualOpen(false)}
      />
    </div>
  );
}
